// frontend/src/pages/richcontenthelpers.js
import React, { useEffect, useRef } from 'react';
import { API_BASE } from '../services/api.js';

// ============================================================================
// MERMAID BLOCK COMPONENT
// ============================================================================

export const MermaidBlock = ({ code, caption }) => {
  const iframeDoc = `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  body { margin: 8px; padding: 0; overflow-x: auto; }
  .mermaid svg { max-width: none; }
</style>
</head>
<body>
<div class="mermaid">${code}</div>
<script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"><\/script>
<script>
  mermaid.initialize({ startOnLoad: true, theme: 'default', flowchart: { useMaxWidth: true } });
  window.addEventListener('load', function() {
    setTimeout(function() {
      var height = document.body.scrollHeight;
      parent.postMessage({ type: 'mermaid-height', height: height }, '*');
    }, 1000);
  });
<\/script>
</body>
</html>`;

  const [height, setHeight] = React.useState(300);

  React.useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'mermaid-height') {
        setHeight(e.data.height + 20);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <div className="rc-flowchart">
      <iframe
        srcDoc={iframeDoc}
        style={{ width: '100%', border: 'none', height: `${height}px` }}
        title="Flowchart"
        sandbox="allow-scripts allow-same-origin"
      />
      {caption && <p className="rc-flowchart-caption">{caption}</p>}
    </div>
  );
};

// ============================================================================
// RENDER INDIVIDUAL BLOCK
// ============================================================================

export const renderBlock = (block) => {
  switch (block.block_type) {

    case 'text':
      return (
        <div key={block.rich_content_id} className="rc-text">
          <p>{block.content_data?.content}</p>
        </div>
      );

    case 'table':
      return (
        <div
          key={block.rich_content_id}
          className={`rc-table rc-table-${block.content_data?.table_style || 'default'}`}
        >
          {block.content_data?.title && (
            <div className="rc-table-title">{block.content_data.title}</div>
          )}
          <div className="rc-table-scroll">
            <table>
              <colgroup>
                {(block.content_data?.columns || []).map((col, i) => (
                  <col key={i} style={{ width: col.width || 'auto' }} />
                ))}
              </colgroup>
              {block.content_data?.has_header && (
                <thead>
                  <tr>
                    {(block.content_data?.columns || []).map((col, i) => (
                      <th key={i}>{col.header}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {(block.content_data?.rows || []).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'steps':
      return (
        <div key={block.rich_content_id} className="rc-steps">
          {block.content_data?.heading && (
            <div className="rc-steps-heading">{block.content_data.heading}</div>
          )}
          {block.content_data?.steps?.length > 0 && (
            <div className="rc-steps-list">
              {block.content_data.steps.map((step, i) => (
                <div key={i} className="rc-step">
                  <div className="rc-step-main">
                    <span className="rc-step-num">Step {i + 1}</span>
                    <span className="rc-step-content">{step.content}</span>
                  </div>
                  {step.explanation && (
                    <div className="rc-step-explanation">{step.explanation}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );

    case 'image':
      return (
        <ImageBlock key={block.rich_content_id} block={block} />
      );

    case 'flowchart':
      return (
        <MermaidBlock
          key={block.rich_content_id}
          code={block.content_data?.mermaid_code || ''}
          caption={block.content_data?.caption || ''}
        />
      );

    case 'html':
      const htmlContent = block.content_data?.html || block.rendered_html || '';
      const iframeDoc = `<!DOCTYPE html>
    <html>
    <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      * { box-sizing: border-box; }
      body { margin: 8px; font-family: Arial, sans-serif; font-size: 14px; }
      table { border-collapse: collapse; width: 100%; max-width: 100%; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
      img { max-width: 100%; height: auto; }
      * { max-width: 100%; }
    </style>
    </head>
    <body>${htmlContent}</body>
    </html>`;

      return (
        <iframe
          key={block.rich_content_id}
          srcDoc={iframeDoc}
          className="rc-html-iframe"
          style={{ width: '100%', border: 'none', minHeight: '200px' }}
          onLoad={(e) => {
            const iframe = e.target;
            const body = iframe.contentDocument?.body;
            if (body) {
              iframe.style.height = body.scrollHeight + 20 + 'px';
            }
          }}
          title="HTML Content"
          sandbox="allow-same-origin"
        />
      );

    case 'video':
      return (
        <div key={block.rich_content_id} className="rc-video">
          <div className={
            block.content_data?.video_type === 'short'
              ? 'rc-video-wrapper-short'
              : 'rc-video-wrapper'
          }>
            <iframe
              src={`https://www.youtube.com/embed/${block.content_data?.youtube_id}${
                block.content_data?.start_seconds
                  ? `?start=${block.content_data.start_seconds}`
                  : ''
              }`}
              frameBorder="0"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              title={block.content_data?.caption || 'Video'}
            />
          </div>
          {block.content_data?.caption && (
            <p className="rc-video-caption">{block.content_data.caption}</p>
          )}
        </div>
      );

    default:
      return null;
  }
};

// ============================================================================
// IMAGE BLOCK WITH ZOOM
// ============================================================================

const ImageBlock = ({ block }) => {
  const [zoomed, setZoomed] = React.useState(false);

  const src = block.content_data?.file_path?.startsWith('http')
    ? block.content_data.file_path
    : `${API_BASE}/${block.content_data?.file_path}`;

  return (
    <>
      <div className="rc-image" onClick={() => setZoomed(true)}>
        <img
          src={src}
          alt={block.content_data?.caption || ''}
          style={{ cursor: 'zoom-in' }}
        />
        {block.content_data?.caption && (
          <p className="rc-image-caption">{block.content_data.caption}</p>
        )}
        <div className="rc-image-zoom-hint">🔍 Tap to zoom</div>
      </div>

      {/* Lightbox overlay */}
      {zoomed && (
        <div className="rc-lightbox" onClick={() => setZoomed(false)}>
          <div className="rc-lightbox-inner">
            <button className="rc-lightbox-close" onClick={() => setZoomed(false)}>✕</button>
            <img src={src} alt={block.content_data?.caption || ''} />
            {block.content_data?.caption && (
              <p className="rc-lightbox-caption">{block.content_data.caption}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const groupBlocks = (blocks) => {
  const groups = [];
  const seen   = new Map();

  blocks.forEach((block, idx) => {
    const label = block.group_label || null;

    if (label) {
      if (seen.has(label)) {
        groups[seen.get(label)].blocks.push(block);
      } else {
        seen.set(label, groups.length);
        groups.push({ key: `group_${label}`, group_label: label, blocks: [block] });
      }
    } else {
      groups.push({ key: `ungrouped_${idx}`, group_label: null, blocks: [block] });
    }
  });

  return groups;
};

export const getGroupConfig = (group) => {
  const types = group.blocks.map(b => b.block_type);

  if (types.includes('video'))     return { icon: '▶️', color: '#e53e3e', gradient: 'linear-gradient(45deg,#e53e3e,#fc8181)' };
  if (types.includes('image'))     return { icon: '📷', color: '#d69e2e', gradient: 'linear-gradient(45deg,#d69e2e,#f6e05e)' };
  if (types.includes('flowchart')) return { icon: '📐', color: '#6b46c1', gradient: 'linear-gradient(45deg,#6b46c1,#9f7aea)' };
  if (types.includes('steps')) return { icon: '🪜', color: '#2b6cb0', gradient: 'linear-gradient(45deg,#2b6cb0,#63b3ed)' };
  if (types.includes('html'))      return { icon: '📄', color: '#2d3748', gradient: 'linear-gradient(45deg,#2d3748,#718096)' };
  if (types.includes('text'))      return { icon: '📝', color: '#744210', gradient: 'linear-gradient(45deg,#744210,#d69e2e)' };
  return                                  { icon: '📌', color: '#2d3748', gradient: 'linear-gradient(45deg,#2d3748,#718096)' };
};

export const getButtonCaption = (group) => {
  if (group.group_label) return group.group_label;
  const types = group.blocks.map(b => b.block_type);
  if (types.includes('video'))     return 'Video';
  if (types.includes('image'))     return 'Image';
  if (types.includes('flowchart')) return 'Diagram';
  if (types.includes('steps')) return 'Steps';
  if (types.includes('html'))      return 'Content';
  return 'More';
};

export const getSectionKey = (group) =>
  `rc_${group.group_label || group.key}`;